import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import {
  useGroupMaterials,
  useMaterialMutations,
  useGroupConfirm,
  useGroupPermissions,
  IGroupMaterial,
} from 'core/hooks';
import { MaterialKindEnum } from 'core/graphql';
import { LoadingIndicator } from 'atoms';

import { GroupColors } from '../parts/groupColors';
import { MaterialEditorPopup } from '../parts/MaterialEditorPopup';
import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../../_detailShared/detail.styled';

interface IMaterialsTabProps {
  groupId: string;
}

const KIND_OPTIONS: { value: '' | MaterialKindEnum; key: string }[] = [
  { value: '', key: 'all' },
  { value: MaterialKindEnum.Homework, key: 'homework' },
  { value: MaterialKindEnum.Reading, key: 'reading' },
  { value: MaterialKindEnum.Video, key: 'video' },
  { value: MaterialKindEnum.Other, key: 'other' },
];

const kindTone = (kind: MaterialKindEnum | null | undefined) => {
  if (kind === MaterialKindEnum.Homework) return 'warn' as const;
  if (kind === MaterialKindEnum.Reading) return 'ok' as const;
  if (kind === MaterialKindEnum.Video) return 'neutral' as const;
  return 'neutral' as const;
};

/**
 * MaterialsTab — list of materials (homework / reading / video /
 * other) attached to lessons of one group. Backed by
 * `materials(groupId)` (TZ §G6).
 *
 * Delete confirmation goes through the global-modal `ConfirmPopup` via
 * `useGroupConfirm` (Req 12.1–12.4): the list refetches only when the
 * delete succeeds (atomic — Req 12.2) and a cancel mutates nothing
 * (Req 12.3). The list also refetches on tab open/re-entry (Req 16.3).
 *
 * Create/edit controls (`MaterialEditorPopup`) are gated behind
 * `useGroupPermissions().canManageMaterials`, which folds in the
 * off-by-default `G6_MATERIALS_CRUD` flag (Req 16.1, 16.4); when enabled
 * they show to operators and the backend authorizes each submission
 * (Req 16.5). A successful create/edit refetches the list (Req 16.2).
 */
export const MaterialsTab = memo(({ groupId }: IMaterialsTabProps) => {
  const { t } = useTranslation();
  const confirm = useGroupConfirm();
  const { canManageMaterials } = useGroupPermissions();
  const [kind, setKind] = useState<'' | MaterialKindEnum>('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<IGroupMaterial | null>(null);

  const { materials, loading, refetch } = useGroupMaterials({
    groupId,
    kind: (kind || undefined) as MaterialKindEnum | undefined,
  });
  const { remove, pending } = useMaterialMutations(() => refetch());

  // Refresh the materials list whenever the tab is opened or re-entered
  // for a (possibly different) group (Req 16.3).
  useEffect(() => {
    refetch();
  }, [refetch, groupId]);

  const groupedByLesson = useMemo(() => {
    const map = new Map<
      string,
      { lessonTitle: string; rows: IGroupMaterial[] }
    >();
    materials.forEach((m) => {
      const key = m.lesson?.id || '__no_lesson__';
      const label = m.lesson
        ? `${m.lesson.lessonNumber ?? m.lesson.number ?? '—'}. ${
            m.lesson.name || ''
          }${
            m.lesson.date
              ? ` · ${moment(m.lesson.date).format('DD.MM.YYYY')}`
              : ''
          }`
        : t('pages.groupDetail.materials.noLesson');
      const bucket = map.get(key) || { lessonTitle: label, rows: [] };
      bucket.rows.push(m);
      map.set(key, bucket);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [materials, t]);

  const onDelete = useCallback(
    async (id: string) => {
      const confirmed = await confirm({
        title: t('pages.groupDetail.materials.confirmDeleteTitle'),
        message: t('pages.groupDetail.materials.confirmDelete'),
        confirmLabel: t('pages.groupDetail.materials.delete'),
        cancelLabel: t('pages.groupDetail.materials.editor.cancel'),
      });
      if (!confirmed) return;
      const ok = await remove(id);
      if (ok) refetch();
    },
    [confirm, remove, refetch, t],
  );

  const openCreate = useCallback(() => {
    setEditing(null);
    setEditorOpen(true);
  }, []);

  const openEdit = useCallback((material: IGroupMaterial) => {
    setEditing(material);
    setEditorOpen(true);
  }, []);

  const closeEditor = useCallback(() => {
    setEditorOpen(false);
    setEditing(null);
  }, []);

  return (
    <S.DimWhenFetching fetching={loading || pending}>
      <S.Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <S.CardTitle style={{ margin: 0 }}>
            {t('pages.groupDetail.materials.title')}
          </S.CardTitle>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {KIND_OPTIONS.map((opt) => (
              <L.PrimaryActionButton
                key={opt.value || 'all'}
                type="button"
                tone={kind === opt.value ? 'primary' : undefined}
                onClick={() => setKind(opt.value)}
              >
                {t(`pages.groupDetail.materials.kind.${opt.key}`)}
              </L.PrimaryActionButton>
            ))}
            {canManageMaterials ? (
              <L.PrimaryActionButton
                type="button"
                tone="primary"
                onClick={openCreate}
              >
                {t('pages.groupDetail.materials.editor.add')}
              </L.PrimaryActionButton>
            ) : null}
          </div>
        </div>

        {materials.length === 0 ? (
          <S.SubTitle style={{ marginTop: 8 }}>
            {loading
              ? t('pages.groupDetail.materials.loading')
              : t('pages.groupDetail.materials.empty')}
          </S.SubTitle>
        ) : (
          <div style={{ marginTop: 12 }}>
            {groupedByLesson.map(([lessonKey, bucket]) => (
              <div key={lessonKey} style={{ marginBottom: 16 }}>
                <L.SubblockHeader>{bucket.lessonTitle}</L.SubblockHeader>
                <L.SmallTable>
                  <thead>
                    <tr>
                      <th>{t('pages.groupDetail.materials.col.kind')}</th>
                      <th>{t('pages.groupDetail.materials.col.title')}</th>
                      <th>{t('pages.groupDetail.materials.col.dueDate')}</th>
                      <th>
                        {t('pages.groupDetail.materials.col.attachments')}
                      </th>
                      <th>{t('pages.groupDetail.materials.col.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bucket.rows.map((m) => (
                      <tr key={m.id}>
                        <td>
                          <L.Tag tone={kindTone(m.kind)}>
                            {t(
                              `pages.groupDetail.materials.kind.${(
                                m.kind || 'other'
                              ).toLowerCase()}`,
                            )}
                          </L.Tag>
                        </td>
                        <td>
                          <div
                            style={{
                              fontWeight: 600,
                              color: GroupColors.textPrimary,
                            }}
                          >
                            {m.title || '—'}
                          </div>
                          {m.description ? (
                            <div
                              style={{
                                fontSize: 11,
                                color: GroupColors.textMuted,
                                marginTop: 2,
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {m.description}
                            </div>
                          ) : null}
                        </td>
                        <td style={{ color: GroupColors.textSecondary }}>
                          {m.dueDate
                            ? moment(m.dueDate).format('DD.MM.YYYY')
                            : '—'}
                        </td>
                        <td>
                          {(m.attachments || []).filter(Boolean).length === 0
                            ? '—'
                            : (m.attachments || [])
                                .filter((a): a is NonNullable<typeof a> =>
                                  Boolean(a),
                                )
                                .map((a, i, arr) => (
                                  <span key={a.id}>
                                    <a
                                      href={a.url || '#'}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{
                                        color: GroupColors.accent,
                                        fontWeight: 600,
                                        textDecoration: 'none',
                                      }}
                                    >
                                      {a.originalName || '—'}
                                    </a>
                                    {i < arr.length - 1 ? ', ' : ''}
                                  </span>
                                ))}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {canManageMaterials ? (
                              <L.PrimaryActionButton
                                type="button"
                                onClick={() => openEdit(m)}
                                disabled={pending}
                              >
                                {t('pages.groupDetail.materials.editor.edit')}
                              </L.PrimaryActionButton>
                            ) : null}
                            <L.PrimaryActionButton
                              type="button"
                              onClick={() => onDelete(m.id)}
                              disabled={pending}
                            >
                              {t('pages.groupDetail.materials.delete')}
                            </L.PrimaryActionButton>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </L.SmallTable>
              </div>
            ))}
          </div>
        )}
      </S.Card>

      {loading && materials.length === 0 ? (
        <S.CenterLoader>
          <LoadingIndicator />
        </S.CenterLoader>
      ) : null}

      {canManageMaterials && editorOpen ? (
        <MaterialEditorPopup
          groupId={groupId}
          material={editing}
          onClose={closeEditor}
          onSaved={() => refetch()}
        />
      ) : null}
    </S.DimWhenFetching>
  );
});
